import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { ClipboardMinor, RiskMinor } from '@shopify/polaris-icons'
import { Button, iconStyles } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { logger } from 'src/utils/logger'

interface Props {
  /** Plaintext, straight from the create response. Null closes the modal. */
  secret: string | null
  onClose(): void
}

const WebhookSecretModalComponent = ({ secret, onClose }: Props) => {
  const snackbar = useSnackbar()

  const handleCopy = useCallback(async () => {
    if (!secret) {
      return
    }

    try {
      await navigator.clipboard.writeText(secret)
      snackbar?.enqueueSnackbar('Signing secret copied', { variant: 'success' })
    } catch (error: unknown) {
      // Never log the secret itself.
      logger.error('Failed to copy webhook signing secret to the clipboard', error)
      snackbar?.enqueueSnackbar('Could not copy — select the secret and copy it manually', {
        variant: 'error',
      })
    }
  }, [secret, snackbar])

  // `CommonModal` forwards `onClose` to MUI's `Modal`, which fires it on a backdrop click and on
  // Escape. This secret cannot be revealed or regenerated, so a stray click outside the dialog
  // would irreversibly lose it. Dismissal is therefore deliberate only: the Done button below.
  // `CommonModal` renders no close "X" of its own when `buttons` is supplied, so nothing else is
  // being neutered here.
  const handleIgnoreImplicitDismiss = useCallback(() => {}, [])

  return (
    <CommonModal
      isOpen={Boolean(secret)}
      title="Signing secret"
      className={tw`max-w-[560px]`}
      onClose={handleIgnoreImplicitDismiss}
      buttons={
        <Button
          size="small"
          color="dark"
          onClick={onClose}
          data-testid="webhook-secret-modal-done-button"
        >
          Done
        </Button>
      }
    >
      <div css={tw`flex flex-row items-start gap-3 rounded-lg bg-warning-100 p-3`}>
        <RiskMinor width={20} css={tw`shrink-0 fill-warning-500`} />
        <Typography variant="body2">
          Copy this now — <b>it cannot be shown again</b>. GigSafe stores it encrypted, and there is
          no way to reveal or regenerate it later. If it is lost, the only fix is a new
          subscription.
        </Typography>
      </div>
      <Typography variant="subtitle2" css={tw`mt-4`}>
        Give this to the customer so they can verify webhook signatures.
      </Typography>
      <div css={tw`mt-2 flex flex-row items-center gap-2`}>
        <Typography
          variant="body2"
          css={tw`min-w-0 flex-1 break-all rounded-lg bg-shades-100 p-3 font-mono`}
          data-testid="webhook-secret-modal-value"
        >
          {secret}
        </Typography>
        <Button
          size="small"
          color="grey"
          leftIcon={<ClipboardMinor width={20} css={iconStyles.grey} />}
          onClick={handleCopy}
          data-testid="webhook-secret-modal-copy-button"
        >
          Copy
        </Button>
      </div>
    </CommonModal>
  )
}

export const WebhookSecretModal = memo(WebhookSecretModalComponent)
